/*
 * dd-storefront-cart.js — Storefront API client for cart operations
 * the AJAX cart API doesn't expose (currently: gift card add/remove).
 *
 * Reads config from `window.__ddSF` (set in layout/theme.liquid):
 *   { token, domain, version, enabled, cartGid }
 *
 * Public API (window.ddSFCart):
 *   apply(code)              -> Promise<{ ok, applied?, all?, cost?, message?, raw? }>
 *   remove(applicationId)    -> Promise<{ ok, all?, cost?, message?, raw? }>
 *   query()                  -> Promise<{ appliedGiftCards, cost, raw } | null>
 *   isReady()                -> bool
 *
 * Why this exists:
 *   Shopify's AJAX cart endpoints (/cart/update.js, /cart/add.js) do NOT
 *   support gift card application. The Storefront GraphQL API does, via
 *   cartGiftCardCodesAdd. Since API 2023-04 the underlying cart resource
 *   is unified across both APIs — a mutation through the Storefront API
 *   on the cart whose id is `gid://shopify/Cart/{{ cart.token }}` is
 *   visible in the AJAX cart on the next /cart.js read and carries
 *   through to /checkout.
 *
 *   Since June 2024, /cart.js returns the token in the form
 *   `<token>?key=<key>`. The `?key=` portion is REQUIRED by the
 *   Storefront API for any mutation; querying without it returns a
 *   redacted cart, mutating returns "The specified cart does not
 *   exist." We pass `{{ cart.token }}` through verbatim — Liquid
 *   exposes the full token-with-key.
 *
 * Error detection:
 *   `cartGiftCardCodesAdd` is intentionally vague about invalid codes
 *   (anti-enumeration hardening): bogus codes return userErrors=[] AND
 *   leave appliedGiftCards unchanged. We detect rejection by comparing
 *   the appliedGiftCards count before vs. after the mutation.
 */
(function () {
  'use strict';

  if (window.ddSFCart) return;

  function cfg() {
    return window.__ddSF || {};
  }

  function isConfigured() {
    var c = cfg();
    return !!(c.enabled && c.token && c.domain && c.version);
  }

  function hasCart() {
    return !!(cfg().cartGid);
  }

  function isReady() {
    return isConfigured() && hasCart();
  }

  function readinessReason() {
    var c = cfg();
    if (!c.enabled) return 'kill switch off (settings.dd_enable_storefront_giftcards or token blank)';
    if (!c.token) return 'storefront token not set in theme settings';
    if (!c.domain) return 'shop domain missing';
    if (!c.version) return 'storefront API version missing';
    if (!c.cartGid) return 'cartGid not yet resolved (call refresh() or apply()/remove() to populate)';
    return 'ready';
  }

  /*
   * The Liquid-rendered `cartGid` in `window.__ddSF` is a snapshot from
   * page-render time. If the user added items via AJAX after page load
   * (the common case for a cart drawer flow), Liquid's snapshot is
   * stale. `refresh()` fetches the current cart token from /cart.js
   * and rebuilds the GID. Cost: 1 request to a same-origin Liquid
   * endpoint (no Cloudflare cross-origin penalty).
   *
   * `apply()` and `remove()` auto-refresh when cartGid is missing so
   * standalone console use works without an explicit refresh call.
   * In the wired-in dd-discount-code.js flow the caller can pass
   * { cartToken } in options to bypass the refresh and avoid the
   * extra request — the AJAX cart response from /cart/update.js
   * already carries the fresh token.
   */
  function refresh(opts) {
    var c = cfg();
    if (!c.domain) return Promise.resolve(null);
    if (opts && typeof opts.cartToken === 'string' && opts.cartToken.length) {
      c.cartGid = 'gid://shopify/Cart/' + opts.cartToken;
      return Promise.resolve(c.cartGid);
    }
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (cart && cart.token) {
          c.cartGid = 'gid://shopify/Cart/' + cart.token;
          return c.cartGid;
        }
        c.cartGid = null;
        return null;
      })
      .catch(function () {
        return c.cartGid;
      });
  }

  function endpoint() {
    var c = cfg();
    return 'https://' + c.domain + '/api/' + c.version + '/graphql.json';
  }

  function gql(query, variables) {
    if (!isReady()) {
      return Promise.reject(new Error('ddSFCart: not ready — ' + readinessReason()));
    }
    return fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg().token,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            var err = new Error('Storefront API HTTP ' + r.status);
            err.status = r.status;
            err.body = t;
            throw err;
          });
        }
        return r.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) {
          var err = new Error(json.errors[0].message || 'Storefront GraphQL error');
          err.graphqlErrors = json.errors;
          throw err;
        }
        return json.data;
      });
  }

  var GQL_CART = [
    'query DDCart($id: ID!) {',
    '  cart(id: $id) {',
    '    id',
    '    appliedGiftCards {',
    '      id',
    '      lastCharacters',
    '      amountUsed { amount currencyCode }',
    '      balance { amount currencyCode }',
    '    }',
    '    cost {',
    '      totalAmount { amount currencyCode }',
    '      subtotalAmount { amount currencyCode }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  /* Gift card add — `warnings` is the field that reveals
     silent-reject reasons that Shopify deliberately omits from
     `userErrors` (anti-enumeration hardening for some codes,
     payments-availability checks for others). The most useful values
     we've seen in production are:
       • PAYMENTS_GIFT_CARDS_UNAVAILABLE — gift cards are configured
         as a payment method that isn't enabled on this store's
         Storefront API surface (checkout still has them, the cart
         layer doesn't). Route to the legacy "apply at checkout"
         handoff and stop trying the SF API path.
       • DISCOUNT_NOT_FOUND — the code doesn't match any active
         gift card. Surface a "code not recognized" message.
       • DISCOUNT_USAGE_LIMIT_REACHED / EXPIRED variants — surface
         specific message instead of the generic handoff. */
  var GQL_GC_ADD = [
    'mutation DDAddGiftCard($cartId: ID!, $codes: [String!]!) {',
    '  cartGiftCardCodesAdd(cartId: $cartId, giftCardCodes: $codes) {',
    '    cart {',
    '      id',
    '      appliedGiftCards {',
    '        id',
    '        lastCharacters',
    '        amountUsed { amount currencyCode }',
    '        balance { amount currencyCode }',
    '      }',
    '      cost {',
    '        totalAmount { amount currencyCode }',
    '        subtotalAmount { amount currencyCode }',
    '      }',
    '    }',
    '    userErrors { field message code }',
    '    warnings { code message target }',
    '  }',
    '}',
  ].join('\n');

  var GQL_GC_REMOVE = [
    'mutation DDRemoveGiftCard($cartId: ID!, $appliedGiftCardIds: [ID!]!) {',
    '  cartGiftCardCodesRemove(cartId: $cartId, appliedGiftCardIds: $appliedGiftCardIds) {',
    '    cart {',
    '      id',
    '      appliedGiftCards {',
    '        id',
    '        lastCharacters',
    '        amountUsed { amount currencyCode }',
    '        balance { amount currencyCode }',
    '      }',
    '      cost {',
    '        totalAmount { amount currencyCode }',
    '        subtotalAmount { amount currencyCode }',
    '      }',
    '    }',
    '    userErrors { field message code }',
    '  }',
    '}',
  ].join('\n');

  function ensureCartGid(opts) {
    /* Fresh cartToken from caller wins over cached page-load cartGid.
       When `dd-discount-code.js` calls apply() it just got a /cart.js
       response that includes the LIVE cart token (with the `?key=...`
       suffix Shopify returns since June 2024). The cached cartGid is
       seeded from `{{ cart.token }}` at page render and can be stale
       in two scenarios we've seen in production:
         1. The cart token rotated between page load and apply. This
            happens when the buyer's session changes (login/logout
            triggers Shopify to re-issue the cart token), or when the
            cart is destroyed + re-created (last item removed then
            new item added on some configurations).
         2. The page rendered with an empty cart (no `cart.token`),
            so `cartGid` was null on first paint, then the buyer
            added an item via AJAX. The page-render snapshot never
            updates without a navigation.
       Either way: if the caller has a fresh token, use it. Also
       reset _appliedCount when cartGid changes — that count was
       computed against whatever cart cartGid was pointing at, and is
       garbage if we just switched carts. */
    var c = cfg();
    if (opts && typeof opts.cartToken === 'string' && opts.cartToken.length) {
      var freshGid = 'gid://shopify/Cart/' + opts.cartToken;
      if (c.cartGid !== freshGid) {
        c.cartGid = freshGid;
        _appliedCount = null;
      }
      return Promise.resolve(freshGid);
    }
    if (c.cartGid && !(opts && opts.forceRefresh)) {
      return Promise.resolve(c.cartGid);
    }
    return refresh(opts);
  }

  /*
   * Cached applied-gift-card count, seeded from Liquid's
   * `cart.applied_gift_cards.size` at page render. Used to detect
   * silent-no-op rejections from cartGiftCardCodesAdd by comparing
   * `appliedGiftCards.length` after the mutation. Re-fetched only when
   * the cache is stale (cleared on configuration change) — saves one
   * Storefront request per apply.
   */
  var _appliedCount = null;

  function getAppliedCount() {
    if (_appliedCount !== null) return Promise.resolve(_appliedCount);
    var seed = cfg().appliedGiftCardsCount;
    if (typeof seed === 'number' && seed >= 0) {
      _appliedCount = seed;
      return Promise.resolve(_appliedCount);
    }
    return query()
      .then(function (c) {
        _appliedCount = c && c.appliedGiftCards ? c.appliedGiftCards.length : 0;
        return _appliedCount;
      })
      .catch(function () {
        _appliedCount = 0;
        return 0;
      });
  }

  function query(opts) {
    if (!isConfigured()) {
      return Promise.resolve(null);
    }
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) return null;
      return gql(GQL_CART, { id: gid }).then(function (data) {
        if (!data || !data.cart) return null;
        return {
          appliedGiftCards: data.cart.appliedGiftCards || [],
          cost: data.cart.cost || null,
          raw: data.cart,
        };
      });
    });
  }

  function apply(code, opts) {
    if (typeof code !== 'string' || !code.trim()) {
      return Promise.resolve({ ok: false, message: 'Please enter a code.' });
    }
    if (!isConfigured()) {
      return Promise.resolve({
        ok: false,
        notReady: true,
        reason: readinessReason(),
      });
    }
    var trimmed = code.trim();
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) {
        return { ok: false, notReady: true, reason: 'no cart token available (cart is empty)' };
      }
      return getAppliedCount().then(function (beforeCount) {
        return gql(GQL_GC_ADD, { cartId: gid, codes: [trimmed] }).then(function (data) {
          var result = (data && data.cartGiftCardCodesAdd) || {};
          var afterCards = (result.cart && result.cart.appliedGiftCards) || [];
          _appliedCount = afterCards.length;
          var addedCount = afterCards.length - beforeCount;
          if (addedCount <= 0) {
            /* Prefer warnings over userErrors for silent-reject
               diagnostics — the 2026-01+ API moved most non-blocking
               cart-side rejections (payments unavailable, recipient
               invalid, discount-not-found masquerading as gift card)
               into the warnings array. userErrors is reserved for
               schema-level / structural failures. */
            var warnings = (result.warnings || []);
            var firstWarn = warnings[0] || null;
            var firstUe = (result.userErrors || [])[0] || null;
            var msg = (firstWarn && firstWarn.message)
              || (firstUe && firstUe.message)
              || null;
            var code = (firstWarn && firstWarn.code)
              || (firstUe && firstUe.code)
              || null;
            return {
              ok: false,
              message: msg,
              code: code,
              warnings: warnings,
              raw: result,
            };
          }
          return {
            ok: true,
            applied: afterCards[afterCards.length - 1],
            all: afterCards,
            cost: result.cart && result.cart.cost,
            raw: result,
          };
        });
      });
    });
  }

  function remove(applicationId, opts) {
    if (!applicationId) return Promise.reject(new Error('ddSFCart.remove: applicationId required'));
    if (!isConfigured()) {
      return Promise.resolve({ ok: false, notReady: true, reason: readinessReason() });
    }
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) {
        return { ok: false, notReady: true, reason: 'no cart token available' };
      }
      return gql(GQL_GC_REMOVE, {
        cartId: gid,
        appliedGiftCardIds: [applicationId],
      }).then(function (data) {
        var result = (data && data.cartGiftCardCodesRemove) || {};
        var afterCards = (result.cart && result.cart.appliedGiftCards) || [];
        _appliedCount = afterCards.length;
        if (result.userErrors && result.userErrors.length) {
          return { ok: false, message: result.userErrors[0].message, raw: result };
        }
        return {
          ok: true,
          all: afterCards,
          cost: result.cart && result.cart.cost,
          raw: result,
        };
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────
   * Client-side cart hydration
   * ────────────────────────────────────────────────────────────────
   *
   * On Shopify Plus configurations like this store's, gift cards
   * applied via the Storefront API persist to the unified cart
   * database (visible at /checkout, the SF API `cart()` query, and
   * subsequent SF mutations) but the AJAX `/cart.js` and Liquid
   * `cart.applied_gift_cards` serializers don't surface them. We
   * confirmed this empirically:
   *   • cartGiftCardCodesAdd → ok:true, cart.appliedGiftCards=[card]
   *   • SF query() → 1 applied, cost.totalAmount = post-gift-card
   *   • /cart.js → applied_gift_cards undefined, total_price unchanged
   *   • /checkout → gift card line present, total reflects deduction
   *
   * Because Liquid is blind, server-side `dd-applied-gift-cards.liquid`
   * renders nothing on first paint AND `/cart?sections=` re-renders
   * also produce empty markup. The painter (`patchDrawerFromSection`)
   * faithfully copies that empty markup over our chips. So we have
   * to drive the gift-card UI client-side from authoritative SF API
   * state.
   *
   * Hydrate runs on:
   *   • DOMContentLoaded — initial paint (in case the buyer arrived
   *     with gift cards already on the cart from a prior session)
   *   • cart:updated event — re-hydrate after every section-render
   *     that may have wiped our chips
   *   • Direct call from dd-discount-code.js after apply/remove —
   *     immediate paint without waiting for the event cascade
   *
   * Hydrate side effects (per call):
   *   • Replaces all `[data-dd-applied-gift-card]` chips inside every
   *     `[data-dd-cart-discounts]` host with chips built from SF API
   *     state (whether the server rendered any or not — SF API is
   *     authoritative).
   *   • Does NOT alter the subtotal. Gift cards are a checkout-time
   *     tender (like store credit) and must not reduce the cart
   *     subtotal, or it would disagree with the line-item total and
   *     the "You saved" figure. The subtotal stays at the merchandise
   *     total painted from /cart.js by cart-paint.js / dd-mini-cart.js.
   *
   * Network cost: 1 Storefront query() per hydrate when the cache is
   * cold; 0 when warm (we re-use the cart payload from the latest
   * apply/remove response by passing it via `opts.cart`).
   */

  function fmtMoneyShort(amountStr, currency) {
    var n = Number(amountStr);
    if (!isFinite(n)) return '';
    /* Match Liquid's `money_without_trailing_zeros` for whole values
       and use 2-decimal precision when there are cents. Currency
       symbol prefix matches the existing chip render in the
       `dd-applied-gift-cards.liquid` snippet (which uses the
       store's `money` filter — same `$` prefix for USD here). For
       non-USD storefronts, fall back to Intl.NumberFormat with the
       returned currencyCode so we don't show a stale prefix. */
    if (currency && currency !== 'USD' && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(n);
      } catch (e) { /* fallback below */ }
    }
    if (n === Math.floor(n)) return '$' + n.toFixed(0);
    return '$' + n.toFixed(2);
  }

  function buildGiftCardChipHTML(card) {
    /* Mirrors the markup of `snippets/dd-applied-gift-cards.liquid`
       so the chips are visually indistinguishable from server-rendered
       ones. Carries `data-dd-client-rendered` so the section painter
       can identify them as ours (and we can replace cleanly). */
    var applicationId = card && card.id ? String(card.id) : '';
    var lastChars = card && card.lastCharacters ? String(card.lastCharacters) : '';
    var ariaLabel = 'Remove gift card ending in ' + lastChars;
    return [
      '<div class="dd-cart-discount dd-cart-discount--giftcard"',
      ' data-dd-applied-gift-card data-dd-client-rendered="true"',
      ' data-application-id="' + applicationId.replace(/"/g, '&quot;') + '"',
      ' data-last-characters="' + lastChars.replace(/"/g, '&quot;') + '">',
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '<rect x="3" y="8" width="18" height="13" rx="2"/>',
      '<path d="M3 12h18"/><path d="M12 8v13"/>',
      '<path d="M7.5 8a2.5 2.5 0 1 1 0-5C9 3 12 8 12 8s3-5 4.5-5a2.5 2.5 0 1 1 0 5"/>',
      '</svg>',
      /* Chip is a removable token only — the amount used now shows in the
         totals breakdown's gift-card money row (checkout-mirror), mirroring
         snippets/dd-applied-gift-cards.liquid. */
      '<span>Gift card &middot;&middot;&middot;&middot;' + lastChars + '</span>',
      '<button type="button" class="dd-cart-discount__remove" data-dd-applied-gift-card-remove aria-label="' + ariaLabel.replace(/"/g, '&quot;') + '">',
      '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">',
      '<path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
      '</svg></button></div>',
    ].join('');
  }

  /* Fold the gift-card deduction straight into the single "Total due" amount.
     The applied gift-card chip above already shows WHICH card is applied, so we
     don't render a separate money row — we just lower the Total-due number.

     Ownership contract: every node we deduct gets a `data-dd-gc-deducted`
     marker. When the card list goes empty we UNDO our own writes — restore the
     marked nodes from their surface's [data-dd-total-cents] stamp (which is
     exactly cart.total_price, the value SSR would show) and drop the marker.
     Nodes we never touched are never written, so the /cart.js painters keep
     ownership of the no-gift-card state. This makes the painter deterministic
     for ALL states: previously the empty-cards case was a no-op that relied on
     a follow-up section render to reset the total — if any stale repaint
     re-deducted after that render, the wrong total stuck forever ("total
     doesn't reset when a gift card is removed").

     Amount due = cart.total_price (stamped on [data-dd-total-cents]) minus the
     gift-card amountUsed — this matches the math the chips imply exactly. We fall
     back to the SF cost.totalAmount only if that stamp is missing. No fetch:
     amountUsed + cost come from the SF query()/apply()/remove() already made. */
  function paintTotalDue(cards, cost) {
    var list = Array.isArray(cards) ? cards : [];

    var helpers = window.ddCartHelpers || {};
    var fmt =
      typeof helpers.formatMoneyWithCurrency === 'function'
        ? helpers.formatMoneyWithCurrency
        : typeof helpers.formatMoney === 'function'
          ? helpers.formatMoney
          : null;

    /* [data-dd-subtotal-money] is a clean text-only node on both surfaces (the
       PRO badge is a sibling, never a child), so textContent is safe.

       CRITICAL: read the total stamp SCOPED to THIS node's own surface via
       closest('[data-dd-total-cents]'). The cart drawer AND the cart-page footer
       each render their own stamp, and both live in the DOM on the cart page. A
       global querySelector returns whichever comes first (the drawer) — but the
       drawer is NOT re-rendered during a cart-page mutation (getRequestSections
       on /cart requests only main-cart-footer), so its stamp goes stale. Using
       that stale value here would compute an old Total due and overwrite the
       correct section-rendered cart-page number — the total would appear to not
       update after a chip/protection change until the drawer eventually
       re-rendered. Per-node scoping keeps the two surfaces independent. */
    var els = document.querySelectorAll('[data-dd-subtotal-money]');

    if (!list.length) {
      /* No gift cards: undo OUR deductions only. A section swap that already
         replaced the node (fresh SSR text, marker gone) is left untouched. */
      for (var r = 0; r < els.length; r++) {
        var rEl = els[r];
        if (!rEl.hasAttribute('data-dd-gc-deducted')) continue;
        rEl.removeAttribute('data-dd-gc-deducted');
        var rCore = rEl.closest ? rEl.closest('[data-dd-total-cents]') : null;
        if (!rCore) continue;
        var rCents = Number(rCore.getAttribute('data-dd-total-cents'));
        if (!isFinite(rCents)) continue;
        var restored = fmt ? fmt(rCents) : fmtMoneyShort(rCents / 100);
        if (rEl.textContent.trim() !== String(restored).trim()) rEl.textContent = restored;
      }
      return;
    }

    var sumUsedCents = 0;
    for (var i = 0; i < list.length; i++) {
      var raw = list[i] && list[i].amountUsed && list[i].amountUsed.amount;
      var c = Math.round(Number(raw || 0) * 100);
      if (isFinite(c)) sumUsedCents += c;
    }

    /* Surface-agnostic fallback from the SF cost — used only when a
       subtotal-money node has no scoped [data-dd-total-cents] stamp. */
    var costCents = (cost && cost.totalAmount && cost.totalAmount.amount != null)
      ? Math.round(Number(cost.totalAmount.amount) * 100)
      : null;

    for (var e = 0; e < els.length; e++) {
      var el = els[e];
      var dueCents = null;
      var coreEl = el.closest ? el.closest('[data-dd-total-cents]') : null;
      if (coreEl) {
        var totalCents = Number(coreEl.getAttribute('data-dd-total-cents'));
        if (isFinite(totalCents)) dueCents = Math.max(0, totalCents - sumUsedCents);
      }
      if (dueCents == null || !isFinite(dueCents)) dueCents = costCents;
      if (dueCents == null || !isFinite(dueCents)) continue;
      var next = fmt ? fmt(dueCents) : fmtMoneyShort(dueCents / 100);
      el.setAttribute('data-dd-gc-deducted', '1');
      if (el.textContent.trim() !== String(next).trim()) el.textContent = next;
    }
  }

  function paintCartFromState(state) {
    /* `state` shape: { appliedGiftCards: [...], cost: {totalAmount, subtotalAmount} }
       Tolerant of partials — null/empty appliedGiftCards just means
       "remove any chips on the page", null cost means "leave totals
       alone". */
    if (!state) return;
    /* Accept both shapes: query() returns `{appliedGiftCards, cost}`,
       apply()/remove() return `{all, cost}`. Same data, two names —
       absorb both so callers don't have to reshape. */
    var cards = state.appliedGiftCards || state.all || [];

    var hosts = document.querySelectorAll('[data-dd-cart-discounts]');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      /* Remove any existing gift-card chips (server-rendered or
         client-rendered from a prior hydrate). SF API state is
         authoritative — we replace wholesale rather than diff. */
      var stale = host.querySelectorAll('[data-dd-applied-gift-card]');
      for (var s = 0; s < stale.length; s++) stale[s].parentNode.removeChild(stale[s]);
      /* Insert fresh chips. Cards are appended at the end of the
         host so the layout follows: discount badge(s) → gift card
         chip(s) → note badge. This matches the snippet's natural
         render order on stores where Liquid does see the cards. */
      for (var c = 0; c < cards.length; c++) {
        var html = buildGiftCardChipHTML(cards[c]);
        var noteBadge = host.querySelector('[data-dd-note-badge]');
        if (noteBadge) {
          noteBadge.insertAdjacentHTML('beforebegin', html);
        } else {
          host.insertAdjacentHTML('beforeend', html);
        }
      }
    }

    /* Gift cards do NOT reduce the Subtotal row — that stays /cart.js-driven
       (the merchandise total after discounts, consistent with the line items).
       Instead, when a gift card is applied, we fold its deduction straight into
       the single "Total due" amount. The chip above already shows which card is
       applied, so no separate money row is rendered. */
    paintTotalDue(cards, state.cost);
  }

  /* In-flight guard so concurrent hydrate() calls don't double-fetch.
     The promise is shared until it resolves; subsequent callers piggy-
     back on it. */
  var _hydratePromise = null;

  /* Last-known authoritative cart state from SF API. Captured after
     every successful query/apply/remove. Used as the "instant paint"
     source on drawer:opened so the chip + subtotal show up at the
     same moment the drawer becomes visible — no flash of wrong state
     while we wait for a fresh network round-trip. The trailing
     network query then validates and corrects (rare). */
  var _lastState = null;

  function captureState(state) {
    if (state && (state.appliedGiftCards || state.all || state.cost)) {
      _lastState = {
        appliedGiftCards: state.appliedGiftCards || state.all || [],
        cost: state.cost || null,
      };
    }
  }

  /* Monotonic state generation. Bumped whenever mutation-derived state is
     applied via hydrate({ state }). A network query() snapshots the generation
     when it STARTS; if a mutation lands while the query is in flight, the
     query's result is a pre-mutation snapshot — capturing/painting it would
     resurrect removed gift cards (chip re-injected, total re-deducted) and
     poison _lastState so every later cached paint repeats the wrong state. */
  var _stateGen = 0;

  function hydrate(opts) {
    if (!isConfigured()) return Promise.resolve();
    /* Fast path: caller already has cart state (e.g. from a recent
       apply/remove response). Skip the network round-trip. */
    if (opts && opts.state) {
      _stateGen++;
      captureState(opts.state);
      paintCartFromState(opts.state);
      return Promise.resolve();
    }
    /* Instant paint from cache: if we have a known state from a
       recent operation, paint it now. The network refresh below
       still runs in case the cache is stale (e.g. another tab
       changed the cart). The two-stage paint is harmless because
       it's idempotent and authoritative-network-state always
       overrides. */
    if (_lastState) {
      paintCartFromState(_lastState);
    }
    if (_hydratePromise) return _hydratePromise;
    var gen = _stateGen;
    _hydratePromise = ensureCartGid(opts)
      .then(function (gid) {
        if (!gid) return null;
        return query(opts);
      })
      .then(function (state) {
        /* Discard stale snapshots: a mutation (apply/remove) committed while
           this query was in flight, and its response state is newer than what
           this query saw. Painting it would undo the mutation in the UI. */
        if (gen !== _stateGen) return;
        captureState(state);
        paintCartFromState(state);
      })
      .catch(function (err) {
        console.warn('[ddSFCart.hydrate] error', err);
      })
      .then(function () {
        _hydratePromise = null;
      });
    return _hydratePromise;
  }

  /* Auto-init: hydrate once on DOMContentLoaded (or immediately if the
     DOM is already past that point), and re-hydrate on every
     cart:updated event. The painter inside `cart-paint.js` runs
     synchronously before `cart:updated` is dispatched, so by the time
     we hydrate, any chips it wiped are still gone — our re-hydrate is
     what restores them.

     The 50ms debounce coalesces rapid bursts (e.g. a quantity change
     dispatches cart:updated then a cascading shipping-protection
     update dispatches it again ~30ms later). */
  var _hydrateDebounceTimer = null;
  function scheduleHydrate() {
    /* Immediate paint from cached SF state — runs SYNCHRONOUSLY inside the
       cart:updated dispatch, i.e. the same frame as the section patch that
       just refreshed [data-dd-total-cents]. Because dispatchEvent invokes
       listeners synchronously and the browser hasn't painted yet, the
       gift-card-adjusted Total is the FIRST thing rendered — no flash of the
       pre-gift-card total, and the gift-card chip is re-injected before paint
       too. paintTotalDue derives from the fresh stamp (not the stale cached
       cost), so the value is correct except in the rare capped-amount case,
       which the debounced network query below then corrects. */
    if (_lastState && isConfigured()) {
      try { paintCartFromState(_lastState); } catch (e) { /* non-fatal: network hydrate below is authoritative */ }
    }
    if (_hydrateDebounceTimer) clearTimeout(_hydrateDebounceTimer);
    _hydrateDebounceTimer = setTimeout(function () {
      _hydrateDebounceTimer = null;
      hydrate();
    }, 50);
  }

  function initHydrator() {
    if (!isConfigured()) return;
    /* Don't run on pages that don't have the discount strip. Saves
       one Storefront query() per pageview on product/collection/etc.
       Note: cart-drawer markup IS present on every page (the drawer
       lives in layout/theme.liquid), so this check passes on PDP /
       collection / search / etc. — which is what we want, because
       opening the drawer from any page must restore the chip. */
    if (!document.querySelector('[data-dd-cart-discounts]')) return;
    hydrate();

    /* Listen on every event the codebase uses to signal a cart UI
       change. We need all three because they don't always fire
       together:
         • cart:updated — the DOM event published by `cart-notify.js`
           after Shopify cart mutations (/cart/update, /cart/change,
           /cart/add, /cart/clear). Catches every real mutation.
         • ddx:cart-mutated — the older alias still emitted by some
           paths (see docs/contracts/events-map.json). Some paths
           emit ddx without cart:updated, so listening to both is
           defensive.
         • drawer:opened — fires on every drawer open. The
           cart-drawer markup is repainted from cached server HTML
           when the drawer opens (no mutation, no cart:updated),
           and that repaint wipes our client-rendered chip. We
           hydrate immediately on open to restore the chip with no
           visible gap. */
    document.addEventListener('cart:updated', scheduleHydrate);
    document.addEventListener('ddx:cart-mutated', scheduleHydrate);
    document.addEventListener('drawer:opened', scheduleHydrate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHydrator);
  } else {
    initHydrator();
  }

  window.ddSFCart = {
    apply: apply,
    remove: remove,
    query: query,
    refresh: refresh,
    hydrate: hydrate,
    isReady: isReady,
    isConfigured: isConfigured,
    hasCart: hasCart,
    readinessReason: readinessReason,
  };
})();
